import React, { useState } from 'react'
import { TextField, IconButton, InputAdornment, alpha, useTheme } from '@mui/material'
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material'
import { Eye, EyeOff } from 'lucide-react'

import { authFieldRootSx } from '../authFieldChrome'
import { authPx } from '../authScale'
import { fieldAutofillResetSx } from '@repo/ui'

export interface AuthPasswordFieldProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  error?: boolean
  helperText?: string
  onKeyPress?: MuiTextFieldProps['onKeyPress']
  autoComplete?: string
  name?: string
}

/**
 * Extracted on its second hand-roll (VERA-1095 — house rule: extract a shared
 * component on the second hand-roll). `SignInPage`'s password-login field
 * originally hand-rolled this exact dark-themed `TextField` + Eye/EyeOff
 * show/hide toggle inline. `PasswordSetup`'s "Create password" and "Confirm
 * password" fields — on this same pure-black auth-page family — needed the
 * identical treatment, each with its OWN independent show/hide state. All
 * three now render this one component.
 *
 * Deliberately NOT built on the shared `@repo/ui`-backed
 * `components/input/fields/TextField.tsx`: that component is coupled to
 * react-hook-form's `register()` contract, and these pages manage their
 * fields with plain `useState`.
 *
 * THEMING lives in `../authFieldChrome`, not here. It used to be declared
 * inline in this file AND in `SignInPage`, from the same tokens — and the two
 * copies drifted, which is how the light-mode hover fill ended up compositing
 * to a dark grey. `authFieldRootSx` is now the single source for both.
 */
export const AuthPasswordField = ({
  value,
  onChange,
  placeholder,
  error,
  helperText,
  onKeyPress,
  autoComplete,
  name,
}: AuthPasswordFieldProps) => {
  const theme = useTheme()
  const [showPassword, setShowPassword] = useState(false)

  return (
    <TextField
      fullWidth
      name={name}
      type={showPassword ? 'text' : 'password'}
      placeholder={placeholder}
      value={value}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      onKeyPress={onKeyPress}
      error={error}
      helperText={helperText}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShowPassword((show) => !show)}
              edge="end"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              // The reveal toggle scales with the field (authScale.ts): MUI's
              // 8px padding and -12px edge pull are fixed px otherwise.
              sx={{
                color: 'text.secondary',
                p: authPx(8),
                mr: authPx(-12),
                '& svg': { width: authPx(18), height: authPx(18) },
              }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{
        '& .MuiInputBase-root': authFieldRootSx(theme),
        // Kill the browser autofill background. Shared with the email field via
        // @repo/ui — this used to be a local copy carrying only the `transition`
        // trick, which does not fire when Chrome autofills on page load, so this
        // field went blue while the email field beside it did not.
        ...fieldAutofillResetSx(theme),
        '& .MuiInputBase-input::placeholder': {
          color: 'text.secondary',
          opacity: 1,
        },
        '& .MuiFormHelperText-root': {
          color: alpha(theme.palette.error.main, 0.9),
          fontSize: authPx(14),
          margin: `${authPx(8)} 0 0 0`,
        },
      }}
    />
  )
}

export default AuthPasswordField
